import type { ThemeSectionInstance } from "@/lib/api/types";
import { LOCKED_SECTION_TYPES } from "./sectionLabels";

/**
 * Reordering the storefront's sections.
 *
 * The stored order IS the array order — there is no `sortOrder` column to
 * normalise here, unlike the floor screen (`docs/restaurant-context.md` §6.5).
 * What that rule is really about is the failure it prevents: a move that looks
 * like it worked and changed nothing. The equivalent trap in an array is moving
 * a row past a LOCKED neighbour, so these helpers report whether a move is
 * possible and the UI disables the control rather than offering a no-op.
 *
 * The masthead is pinned first and the footer last, which is not decoration:
 * the theme validator enforces exactly that (`header_position` /
 * `footer_position`), so a draft that moved either would be rejected on save
 * with an error the seller cannot act on.
 */

/** Indices a section may actually be moved between — locked ends excluded. */
function movableRange(sections: ThemeSectionInstance[]): [number, number] {
  let first = 0;
  let last = sections.length - 1;
  while (first < sections.length && LOCKED_SECTION_TYPES.has(sections[first].type)) {
    first += 1;
  }
  while (last >= 0 && LOCKED_SECTION_TYPES.has(sections[last].type)) {
    last -= 1;
  }
  return [first, last];
}

export function canMove(
  sections: ThemeSectionInstance[],
  index: number,
  direction: -1 | 1,
): boolean {
  const section = sections[index];
  if (!section || LOCKED_SECTION_TYPES.has(section.type)) return false;
  const target = index + direction;
  const [first, last] = movableRange(sections);
  return target >= first && target <= last;
}

/** Moves one section one place, or returns the input untouched. */
export function moveSection(
  sections: ThemeSectionInstance[],
  index: number,
  direction: -1 | 1,
): ThemeSectionInstance[] {
  if (!canMove(sections, index, direction)) return sections;
  const next = [...sections];
  const target = index + direction;
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

/**
 * Flips one section's visibility.
 *
 * Writes an explicit `visible: true` rather than deleting the key when turning
 * one back on. Absent and `true` render identically, but a seller who hides a
 * section and shows it again should see the same JSON they would get from a
 * fresh apply — an implicit value here would make two equivalent states look
 * different in the draft diff and in every log that prints it.
 */
export function toggleSection(
  sections: ThemeSectionInstance[],
  index: number,
): ThemeSectionInstance[] {
  const section = sections[index];
  if (!section || LOCKED_SECTION_TYPES.has(section.type)) return sections;
  return sections.map((entry, i) =>
    i === index ? { ...entry, visible: entry.visible === false } : entry,
  );
}

/** Absent means visible — the same default the rest of the theme system uses. */
export function isVisible(section: ThemeSectionInstance): boolean {
  return section.visible !== false;
}
